import React, { useRef, useState, useMemo, useEffect } from "react";
import { useScreenDimensions, useDevice } from "../_layout";

import { Text, View, TouchableOpacity, ScrollView } from "react-native";
import GlobalStyles from "../../Resources/Styles";
import Colors from "../../Resources/Color";
import Fonts from "../../Resources/Fonts";
import { useTranslation } from "react-i18next";
import { Ionicons } from "@expo/vector-icons";

import { observer } from "mobx-react";
import { Menu, MenuItem } from "react-native-material-menu";

import SearchBar from "../Components/SearchBar";
import { audioFormatTime } from "../utils/numberUtils";
import ChatManager from "../Classes/ChatManager";
import IconEmptyView from "../Components/IconEmptyView";
import ClientNameLogo from "./ClientNameLogo";
import MediaManager from "../Classes/MediaManager";
import AudioManager from "../Classes/AudioManager";

const ChatListView = observer(({ coach, fullScreen }) => {
  const { screenSize } = useScreenDimensions();
  const { t, i18n } = useTranslation();
  const { isMobile } = useDevice();
  const chatManager = ChatManager.getInstance();
  const mediaManager = MediaManager.getInstance();
  const audioManager = AudioManager.getInstance();
  const clients = coach.getFilteredChatClients();
  const selectedClientId = coach.selectedChatId;

  const ClientRow = observer(({ client }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [menuVisible, setMenuVisible] = useState(false);
    const [isMenuItemHovered, setIsMenuItemHovered] = useState(false);

    const showMenu = () => setMenuVisible(true);
    const hideMenu = () => {
      setIsHovered(false);
      setMenuVisible(false);
    };

    const handleMarkAsUnread = async () => {
      client.setProperty("unreadMark", true);
      await client.uploadUnreadMark();
      hideMenu();
    };

    return (
      <View
        style={{
          flexDirection: "row",
          width: "100%",
          height: 52,
          paddingHorizontal: 8,
          marginVertical: 2,
          alignItems: "center",
          borderRadius: 16,
          backgroundColor: isHovered
            ? "rgba(0, 0, 0, 0.05)"
            : selectedClientId === client.id
            ? "rgba(0, 0, 0, 0.07)"
            : "transparent",
          opacity: client.isAvailable ? 1 : 0.5,
        }}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <TouchableOpacity
          onPress={() => {
            if (audioManager.isRecording) {
              return;
            }
            coach.setProperty("selectedChatId", client.id);
            chatManager.setProperty("selectedReplyToId", null);
            mediaManager.closeMediaPlayer();
          }}
          onLongPress={() =>
            !client.unreadMark && client.unreadCount === 0 && showMenu()
          }
          style={{
            flex: 1,
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <ClientNameLogo client={client} isForChat={false} />

          <View
            style={{
              alignItems: "flex-start",
              flex: 1,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: 8,
              }}
            >
              <Text
                style={{
                  color: Colors.blackColor,
                  fontSize: 14,
                  fontWeight: "500",
                  flexShrink: 1,
                }}
                numberOfLines={1}
              >
                {client.nameForCoach} {client.surnameForCoach}
              </Text>

              {client.getLastMessage && (
                <Text
                  style={{
                    color: Colors.grayColor,
                    fontSize: 11,
                    flexShrink: 0,
                  }}
                  numberOfLines={1}
                >
                  {client.getLastMessage.timeAgo(t, i18n.language)}
                </Text>
              )}
            </View>

            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                marginTop: 2,
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              {client.getLastMessage ? (
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    flexShrink: 1,
                    marginRight: 8,
                  }}
                >
                  {/* Audio Message */}
                  {client.getLastMessage.type === "audio" && (
                    <>
                      <Ionicons
                        name="mic-outline"
                        size={12}
                        color={Colors.grayColor}
                        style={{ marginRight: 4 }}
                      />
                      <Text
                        style={{
                          fontSize: 11,
                          color: Colors.grayColor,
                          flexShrink: 1,
                        }}
                      >
                        {audioFormatTime(client.getLastMessage.totalTime)}
                      </Text>
                    </>
                  )}

                  {/* Image Message */}
                  {client.getLastMessage.type === "image" && (
                    <>
                      <Ionicons
                        name="camera-outline"
                        size={12}
                        color={Colors.grayColor}
                        style={{ marginRight: 4 }}
                      />
                      <Text
                        style={{
                          fontSize: 11,
                          color: Colors.grayColor,
                          flexShrink: 1,
                          width: "100%",
                        }}
                        numberOfLines={1}
                      >
                        {client.getLastMessage.text || t("photo-string")}
                      </Text>
                    </>
                  )}

                  {/* Video Message */}
                  {client.getLastMessage.type === "video" && (
                    <>
                      <Ionicons
                        name="videocam-outline"
                        size={12}
                        color={Colors.grayColor}
                        style={{ marginRight: 4 }}
                      />
                      <Text
                        style={{
                          fontSize: 11,
                          color: Colors.grayColor,
                          flexShrink: 1,
                        }}
                      >
                        {client.getLastMessage.totalTime
                          ? `${t("video-string")} - ${Math.floor(
                              client.getLastMessage.totalTime
                            )}s`
                          : t("video-string")}
                      </Text>
                    </>
                  )}

                  {/* Text Message */}
                  {client.getLastMessage.type === "text" && (
                    <Text
                      style={{
                        fontSize: 11,
                        color: Colors.grayColor,
                        flexShrink: 1,
                        width: "100%",
                      }}
                      numberOfLines={1}
                    >
                      {client.getLastMessage.text || ""}
                    </Text>
                  )}
                </View>
              ) : (
                <View
                  style={{
                    flexShrink: 1,
                  }}
                >
                  <Text
                    style={{
                      color: Colors.grayColor,
                      fontSize: 11,
                    }}
                    numberOfLines={1}
                  >
                    {client.email}
                  </Text>
                </View>
              )}
              <View
                style={{ flexDirection: "row", alignItems: "center", gap: 4 }}
              >
                {client.unreadCount > 0 ? (
                  <Text
                    style={{
                      color: Colors.lightGrayColor,
                      backgroundColor: Colors.bubbleColor,
                      borderRadius: 12,
                      alignSelf: "center",
                      textAlign: "center",
                      textAlignVertical: "center",
                      fontSize: 11,
                      width: 20,
                      minWidth: 20, //To avoid being compressed when preview text is large
                      height: 20,
                      lineHeight: 20,
                    }}
                  >
                    {client.unreadCount <= 99 ? client.unreadCount : 99}
                  </Text>
                ) : (
                  client.unreadMark && (
                    <View
                      style={{
                        // color: colors.whiteBubbleColor,
                        backgroundColor: Colors.bubbleColor,
                        borderRadius: 12,
                        alignSelf: "center",
                        textAlign: "center",
                        textAlignVertical: "center",
                        width: 12,
                        height: 12,
                      }}
                    />
                  )
                )}
                {!client.unreadMark && client.unreadCount === 0 && (
                  <Menu
                    visible={menuVisible}
                    anchor={
                      isHovered ? (
                        <TouchableOpacity
                          onPress={showMenu}
                          style={{ height: 12 }}
                        >
                          <Ionicons
                            name="chevron-down"
                            size={16}
                            color={Colors.grayColor}
                          />
                        </TouchableOpacity>
                      ) : (
                        <View style={{ width: 0, height: 0 }} />
                      )
                    }
                    style={[GlobalStyles.menu]}
                    onRequestClose={hideMenu}
                  >
                    <MenuItem
                      onPress={handleMarkAsUnread}
                      style={{ minWidth: 190 }}
                      textStyle={{
                        fontSize: 14,
                        fontWeight: isMenuItemHovered ? "600" : "normal",
                      }}
                      onMouseEnter={() =>
                        !isMobile && setIsMenuItemHovered(true)
                      }
                      onMouseLeave={() => setIsMenuItemHovered(false)}
                    >
                      <Text selectable={false}>{t("markAsUnread-string")}</Text>
                    </MenuItem>
                  </Menu>
                )}
              </View>
            </View>
          </View>
        </TouchableOpacity>
      </View>
    );
  });

  return (
    <View style={{ flex: 1, paddingBottom: 0 }}>
      <>
        <View
          style={{
            marginHorizontal: 12,
            marginBottom: 8,
          }}
        >
          <SearchBar
            text={coach.searchTermChats}
            onChangeText={(text) => coach.setProperty("searchTermChats", text)}
            dark={false}
            placeholder={"searchClient-string"}
            maxWidth={"100%"}
          />
        </View>
        {clients.length > 0 ? (
          <ScrollView
            style={{ flex: 1, paddingHorizontal: fullScreen ? 12 : 4 }}
            showsVerticalScrollIndicator={false}
          >
            {coach.getFilteredChatClients().map((client, index, array) => (
              <View key={client.id}>
                <ClientRow client={client} />
                {index !== array.length - 1 && (
                  <View
                    style={{
                      height: 1,
                      backgroundColor: "rgba(0, 0, 0, 0.1)",
                      marginHorizontal: 12,
                    }}
                  />
                )}
              </View>
            ))}
          </ScrollView>
        ) : (
          <IconEmptyView
            iconLibrary="Ionicons"
            iconName="people-outline"
            mainText="emptyClientsTitle-string"
            subText="noAthletesFoundText-string"
            marginTop={34}
            marginBottom={16}
          />
        )}
      </>
    </View>
  );
});

export default ChatListView;
